import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';
import { APP_CONFIG, Settings } from 'src/app/config/settings';
import { WINDOW } from 'src/app/core/core.module';
import { combine } from 'src/app/shared/utilities/route';
import { ContractRepipelineInfoModel } from 'src/app/travel-contracts/travel-contracts-grid/models/contract-repipeline-Info.model';
import { ContractRepipelineSaveModel } from 'src/app/travel-contracts/travel-contracts-grid/models/contract-repipeline-save.model';
import { ContractRepipelineUpdateModel } from 'src/app/travel-contracts/travel-contracts-grid/models/contract-repipeline-update.model';
import { MyAyaContractItem } from 'src/app/travel-contracts/travel-contracts-grid/models/myaya-contract-model';
import { RepipelinedContract } from 'src/app/travel-contracts/travel-contracts-grid/models/repipelined-contract.model';

@Injectable({
    providedIn: 'root'
})
export class ContractRepipeService {
    private readonly coreApiPath: string;

    constructor(
        private readonly http: HttpClient,
        @Inject(APP_CONFIG) settings: Settings,
        @Inject(WINDOW) private readonly window: Window
    ) {
        this.coreApiPath = combine(settings.coreAPIUrl, 'ayanova/contract-repipeline');
    }

    getContractInfo(contractId: number): Observable<MyAyaContractItem> {
        return this.http.get<MyAyaContractItem>(`${this.coreApiPath}/${contractId}/contract-info`);
    }

    saveRepipeline(contractRepipelineSave: ContractRepipelineSaveModel): Observable<ContractRepipelineInfoModel> {
        return this.http.post<ContractRepipelineInfoModel>(
            `${this.coreApiPath}/save-repipeline`,
            contractRepipelineSave
        );
    }

    getRepipelinedContracts(contractRepipelinedId: number): Observable<RepipelinedContract> {
        return this.http.get<RepipelinedContract>(`${this.coreApiPath}/${contractRepipelinedId}`);
    }

    removeContractRepipelineLink(contractRepipelineId: number): Observable<RepipelinedContract> {
        return this.http.delete<RepipelinedContract>(`${this.coreApiPath}/${contractRepipelineId}`);
    }

    updateContractRepipelineLink(
        contractRepipelineUpdate: ContractRepipelineUpdateModel
    ): Observable<ContractRepipelineInfoModel> {
        return this.http.put<ContractRepipelineInfoModel>(`${this.coreApiPath}/update`, contractRepipelineUpdate);
    }

    getDocumentRepipe(contractId: number, documentIdNew: number, documentTypeId: number): Observable<any> {
        const urlRepipe = `${this.coreApiPath}/${contractId}/documents?documentIdNew=${documentIdNew}&documentTypeId=${documentTypeId}`;
        return this.http.get(urlRepipe, { responseType: 'blob' }).pipe(
            map((file) => {
                const url = this.window.window.URL.createObjectURL(file);
                return { file, url };
            })
        );
    }

    hasContractBeenRepipelinedByRepipelinedContractId(repipelinedContractId: number): Observable<boolean> {
        return this.http.get<boolean>(`${this.coreApiPath}/${repipelinedContractId}/is-repiped`);
    }

    openDocumentRepipe(
        contractId: number,
        documentIdNew: number,
        documentTypeId: number,
        windowId: string,
        windowPositionTop?: number,
        windowPositionLeft?: number
    ): Observable<Window> {
        // Generate a random handle ID to guarantee a new window opens
        const windowHandleId = `${windowId}_${this.getRandomArbitrary()}`;
        return this.getDocumentRepipe(contractId, documentIdNew, documentTypeId).pipe(
            map((file) => {
                const viewerUrl = `/assets/document-viewer.html?path=${encodeURIComponent(file.url)}`;
                return this.window.open(
                    viewerUrl,
                    windowHandleId,
                    `height=${this.window.outerHeight * 0.8},width=${this.window.outerWidth * 0.5},
                    top=${windowPositionTop ? windowPositionTop : 0},left=${
                        windowPositionLeft ? windowPositionLeft : 0
                    }`
                );
            })
        );
    }

    private getRandomArbitrary(min?, max?): number {
        min = min ? min : 1;
        max = max ? max : Number.MAX_SAFE_INTEGER;
        return Math.random() * (max - min) + min;
    }
}
